import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Notification,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconExternalLink } from "@tabler/icons-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getProduct } from "@utils/api-communication/RestApiCli";
import { IProduct, ManagementConfig, ProductConfig } from "types/productManagementTypes";
import { emptyManagementConfig } from "./ManagementConfigForm";
import { ProductFetchStatus } from "./DeviceStatesPanel";
import ProductConfigPanels, { ConfigTabValue, ProductConfigTabList } from "./ProductConfigPanels";
import ProductDetailsPanel from "./ProductDetailsPanel";
import useProductConfigActions from "./useProductConfigActions";
import { useDeviceConfigs } from "./useDeviceConfigs";

type StatusTabValue = "details" | ConfigTabValue;

const ProductStatusPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pid = searchParams.get("pid") ?? "";
  const navState = location.state as { from?: string; fromLabel?: string } | null;
  const backTo = navState?.from;
  const backLabel = navState?.fromLabel ? `Back to ${navState.fromLabel}` : "Back";

  const [tab, setTab] = useState<StatusTabValue>("details");
  const [product, setProduct] = useState<IProduct | null>(null);
  const [productStatus, setProductStatus] = useState<ProductFetchStatus>("loading");

  const { submitting, notification, setNotification, pushConfig, sendPlayerPref, resetConfig } =
    useProductConfigActions();

  const [reloadKey, setReloadKey] = useState(0);
  const reloadTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    },
    [],
  );

  const reloadAfterDeviceReports = () => {
    if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      setReloadKey((k) => k + 1);
    }, 1500);
  };
  const {
    applyConfig: config,
    otherEntries,
    loading: configLoading,
    failed: configsFailed,
  } = useDeviceConfigs(pid, reloadKey);

  useEffect(() => {
    if (!pid) {
      setProductStatus("error");
      return;
    }
    let cancelled = false;
    setProduct(null);
    setProductStatus("loading");

    getProduct(
      pid,
      (response) => {
        if (cancelled) return;
        setProduct(response?.data ?? null);
        setProductStatus("loaded");
      },
      () => {
        if (!cancelled) setProductStatus("error");
      },
    );

    return () => {
      cancelled = true;
    };
  }, [pid, reloadKey]);

  const handlePush = (cfg: ManagementConfig) =>
    pushConfig(pid, cfg, () => setReloadKey((k) => k + 1));

  const handleReset = (resetPid: string) => resetConfig(resetPid, () => setReloadKey((k) => k + 1));

  // Pushing from Device settings creates the entry for a device that has none.
  const effectiveConfig: ProductConfig = config ?? { pid, config: emptyManagementConfig() };

  if (!pid) {
    return (
      <Container size="lg" pb={120}>
        <Title order={2} my="md">
          Product Status
        </Title>
        <Alert color="red" variant="light" title="No product id">
          Open this page with a <strong>pid</strong> query parameter, for example
          /product-status?pid=&lt;uuid&gt;.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" pb={120}>
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md" my="md">
        <Stack gap={0}>
          <Title order={2}>Product Status</Title>
          <Text size="sm" c="dimmed">
            {pid}
          </Text>
        </Stack>
        <Group gap="sm" wrap="wrap">
          {backTo && (
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate(backTo)}
              data-testid="product-status-back-link"
            >
              {backLabel}
            </Button>
          )}
          <Button
            variant="light"
            rightSection={<IconExternalLink size={16} />}
            onClick={() => navigate(`/version-control?pid=${encodeURIComponent(pid)}`)}
            data-testid="product-status-version-control-link"
          >
            Version Control
          </Button>
        </Group>
      </Group>

      {notification && (
        <Notification
          color={notification.type === "success" ? "green" : "red"}
          onClose={() => setNotification(null)}
          withCloseButton
          mb="md"
        >
          {notification.msg}
        </Notification>
      )}

      {productStatus === "error" && (
        <Alert color="red" variant="light" title="Could not load this product" mb="md">
          The product details could not be fetched. Device state and default config comparisons are
          unavailable.
        </Alert>
      )}

      {!configLoading && !config && (
        <Alert color="blue" variant="light" title="No saved config" mb="md">
          This device has no stored configuration yet. Saving from the Device settings or Wi-Fi tab
          creates one and pushes it to the device.
        </Alert>
      )}

      {configLoading ? (
        <Center style={{ height: "200px" }}>
          <Loader />
        </Center>
      ) : (
        <Tabs value={tab} onChange={(v) => v && setTab(v as StatusTabValue)}>
          <Tabs.List>
            <Tabs.Tab value="details">Product details</Tabs.Tab>
            <ProductConfigTabList hasPid={!!pid} />
          </Tabs.List>

          <Tabs.Panel value="details" pt="md">
            {product ? (
              <ProductDetailsPanel product={product} />
            ) : productStatus === "loading" ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : (
              <Text size="sm" c="dimmed">
                No product details available for this id.
              </Text>
            )}
          </Tabs.Panel>

          <ProductConfigPanels
            config={effectiveConfig}
            product={product}
            productStatus={productStatus}
            submitting={submitting}
            onPush={handlePush}
            onPlayerPref={(key, value) => sendPlayerPref(pid, key, value, reloadAfterDeviceReports)}
            onReset={handleReset}
            otherEntries={otherEntries}
            otherEntriesFailed={configsFailed}
            hasSavedConfig={!!config}
          />
        </Tabs>
      )}
    </Container>
  );
};

export default ProductStatusPage;
